/**
 * TranslationPreviewModal - Preview and commit/download translated subtitle
 *
 * Shows translation results with:
 * - Summary stats (cues, characters, estimated cost)
 * - Side-by-side preview of original vs translated
 * - Download for review OR commit to title options
 */

/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */

import { authenticatedFetch } from "@lumi/data";
import { CheckCircle2, Download, Languages, Loader2, Upload, X } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import type { TranslationResult } from "./TranslateSubtitleModal";

/** Get language display name */
function getLanguageLabel(code: string): string {
  const labels: Record<string, string> = {
    BG: "Bulgarian",
    CS: "Czech",
    DA: "Danish",
    DE: "German",
    EL: "Greek",
    EN: "English",
    "EN-GB": "English (UK)",
    "EN-US": "English (US)",
    ES: "Spanish",
    ET: "Estonian",
    FI: "Finnish",
    FR: "French",
    HU: "Hungarian",
    ID: "Indonesian",
    IT: "Italian",
    JA: "Japanese",
    KO: "Korean",
    LT: "Lithuanian",
    LV: "Latvian",
    NL: "Dutch",
    PL: "Polish",
    "PT-BR": "Portuguese (Brazil)",
    "PT-PT": "Portuguese (Portugal)",
    RO: "Romanian",
    RU: "Russian",
    SK: "Slovak",
    SL: "Slovenian",
    SV: "Swedish",
    TR: "Turkish",
    UK: "Ukrainian",
    ZH: "Chinese",
  };
  return labels[code.toUpperCase()] || code;
}

interface TranslationPreviewModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly subtitleId: string | null;
  readonly result: TranslationResult | null;
  readonly onCommitSuccess: () => void;
}

export function TranslationPreviewModal({
  open,
  onOpenChange,
  subtitleId,
  result,
  onCommitSuccess,
}: TranslationPreviewModalProps): ReactElement {
  const { accessToken, refreshAuth } = useAuth();
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  const [committing, setCommitting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setIsClosing(false);
    }
  }, [open]);

  if (!open && !isClosing) return <></>;
  if (!result) return <></>;

  const targetLabel = getLanguageLabel(result.targetLanguage);

  const handleClose = () => {
    if (committing) return;
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onOpenChange(false);
    }, 200);
  };

  const isVisible = open && !isClosing;

  /**
   * Download VTT file for review
   */
  const handleDownload = (): void => {
    const blob = new Blob([result.translatedVtt], { type: "text/vtt" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subtitle-${result.targetLanguage.toLowerCase()}.vtt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("VTT file downloaded for review");
  };

  /**
   * Commit translation to Cloudflare Stream
   */
  const handleCommit = async (): Promise<void> => {
    if (!subtitleId || !accessToken) return;

    setCommitting(true);

    try {
      const response = await authenticatedFetch<{
        success: boolean;
        data?: {
          subtitleId: string;
          language: string;
          label: string;
          url: string;
        };
        error?: string;
        message?: string;
      }>(
        `${apiBaseUrl}/v1/admin/subtitles/${subtitleId}/translate/commit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            tempStorageKey: result.tempStorageKey,
            label: targetLabel,
          }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      if (response.success) {
        toast.success(`${targetLabel} subtitle added to title`);
        onOpenChange(false);
        onCommitSuccess();
      } else {
        toast.error(response.message || response.error || "Failed to add subtitle");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to add subtitle";
      toast.error(message);
    } finally {
      setCommitting(false);
    }
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[60] flex items-center justify-center ${isVisible ? "animate-in fade-in duration-200" : "animate-out fade-out duration-200"}`}
    >
      {/* Backdrop overlay */}
      <button
        type="button"
        className={`absolute inset-0 bg-black/50 cursor-default ${isVisible ? "animate-in fade-in duration-200" : "animate-out fade-out duration-200"}`}
        onClick={handleClose}
        aria-label="Close modal"
        disabled={committing}
      />

      {/* Modal content */}
      <div
        className={`relative bg-neutral-800/20 backdrop-blur-lg border border-neutral-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col ${isVisible ? "animate-in zoom-in-95 fade-in duration-200" : "animate-out zoom-out-95 fade-out duration-200"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-800">
          <div>
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Translation Complete
            </h2>
            <p className="text-sm text-neutral-400 mt-1">
              Review the translation and choose to download or add to title
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={committing}
            className="text-neutral-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 py-4 border-b border-neutral-800">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{result.cueCount}</div>
              <div className="text-xs text-neutral-500">Cues Translated</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{result.characterCount.toLocaleString()}</div>
              <div className="text-xs text-neutral-500">Characters</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-500">${result.estimatedCost.usdEstimate.toFixed(2)}</div>
              <div className="text-xs text-neutral-500">Est. Cost</div>
            </div>
          </div>

          {/* Language badge */}
          <div className="flex items-center gap-2 py-2">
            <Languages className="h-4 w-4 text-neutral-500" />
            <span className="text-sm text-neutral-400">
              {getLanguageLabel(result.sourceLanguage)} → {targetLabel}
            </span>
          </div>

          {/* Preview Table */}
          <div className="overflow-auto border border-neutral-800 rounded max-h-64">
            <table className="w-full text-sm">
              <thead className="bg-neutral-900 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-neutral-500 font-medium w-24">Time</th>
                  <th className="px-3 py-2 text-left text-neutral-500 font-medium">Original</th>
                  <th className="px-3 py-2 text-left text-neutral-500 font-medium">Translated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {result.preview.map((item) => (
                  <tr key={item.index} className="hover:bg-neutral-900/50">
                    <td className="px-3 py-2 text-neutral-500 font-mono text-xs">{item.startTime.substring(3, 8)}</td>
                    <td className="px-3 py-2 text-neutral-300">{item.originalText}</td>
                    <td className="px-3 py-2 text-white">{item.translatedText}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {result.cueCount > 10 && (
              <div className="px-3 py-2 text-center text-xs text-neutral-500 bg-neutral-900/50">
                Showing first 10 of {result.cueCount} cues
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-neutral-800">
          <button
            type="button"
            onClick={handleClose}
            disabled={committing}
            className="px-4 py-2 text-sm text-neutral-300 hover:text-white border border-neutral-700 hover:border-neutral-600 rounded transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={committing}
            className="px-4 py-2 text-sm text-neutral-300 hover:text-white border border-neutral-700 hover:border-neutral-600 rounded transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Download for Review
          </button>
          <button
            type="button"
            onClick={() => void handleCommit()}
            disabled={committing}
            className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-500 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {committing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Add to Title
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
